import React, { ChangeEvent, Component, KeyboardEvent } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { delay } from "@formant/core/common/delay";
import { ITextTypeFieldProps } from "@formant/ui/component/common/Field";
import { IconName } from "@formant/ui/component/common/Icon";
import { TextField } from "@formant/ui/component/common/TextField";

import * as styles from "./AutocompleteField.scss";
import { Menu } from "./Menu";
import { IMenuItem } from "./Menu/IMenuItem";

export interface IAutocompleteItem<T> {
    value: T;
    label?: string;
    icon?: IconName;
    selected?: boolean;
}

const itemLabel = ({ label, value }: IAutocompleteItem<unknown>) =>
    label || `${value}`;

export interface IAutocompleteFieldProps<T> {
    items: IAutocompleteItem<T>[];
    className?: string;
    onSelected: (value: T) => void;
    onEnter?: (value: string) => boolean;
}

@observer
export class AutocompleteField<T> extends Component<
    IAutocompleteFieldProps<T> & ITextTypeFieldProps<HTMLInputElement>
> {
    @observable
    private value: string = "";
    @observable
    private open: boolean = false;

    public render() {
        const { items, onSelected, className, ...props } = this.props;
        const value = this.value.toLocaleLowerCase();
        const matches = items
            .filter(_ => {
                const label = itemLabel(_).toLocaleLowerCase();
                return (
                    label.startsWith(value) ||
                    (value.length >= 3 && label.includes(value))
                );
            })
            .sort((a, b) => itemLabel(a).localeCompare(itemLabel(b)))
            .slice(0, 5);
        const options = matches.map<IMenuItem<T>>(item => ({
            ...item,
            key: item.label,
            onSelected: this.onSelected,
        }));
        return (
            <TextField
                {...props}
                value={this.value}
                className={classnames(styles["autocomplete-field"], className)}
                onChange={this.onChange}
                onFocus={this.onFocus}
                onClick={this.onFocus}
                onBlur={this.onClose}
                onKeyDown={this.onKeyDown}
            >
                <Menu
                    visible={this.open}
                    onClose={this.onClose}
                    items={options}
                    autofocus={false}
                    className={styles.menu}
                />
            </TextField>
        );
    }

    private onChange = (event: ChangeEvent<HTMLInputElement>) => {
        this.value = event.target.value;
        this.open = true;
    };

    private onKeyDown = async (event: KeyboardEvent<HTMLInputElement>) => {
        const { onEnter } = this.props;
        if (!onEnter) {
            return;
        }
        if (event.key !== "Enter") {
            return;
        }
        event.preventDefault();
        await delay();
        if (this.value) {
            if (onEnter(this.value)) {
                this.value = "";
                this.open = false;
            }
        }
    };

    private onFocus = () => {
        this.open = true;
    };

    private onSelected = (value: T) => {
        this.props.onSelected(value);
        this.open = false;
        this.value = "";
    };

    private onClose = () => {
        this.open = false;
    };
}
